import {MouseEvent, Suspense, useEffect, useRef, useState} from "react";
import {css, cx} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {CalendarCheckIcon, FiltersIcon, HorizontalListIcon, PropertyPlanIcon, SortIcon} from "@pg-design/icons-module";
import {Image} from "@pg-design/image-module";
import {Loader} from "@pg-design/loader-module";
import {Pagination, PaginationSize as PS} from "@pg-design/pagination-module";
import {Highlight, Text} from "@pg-design/text-module";
import {trackAmplitudeEvent} from "@pg-mono/amplitude";
import {fromQueryValues, toQueryValues} from "@pg-mono/form";
import {isEmpty, omit} from "@pg-mono/nodash";
import {IQueryValue} from "@pg-mono/request";
import {dateTimeFormat, formatFutureDate, numberFormat} from "@pg-mono/string-utils";
import {BUTTONS_TEST_ID} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";

import {usePrimaryClick} from "../../../hooks/use_primary_click";
import {useResponsiveLinkTarget} from "../../../hooks/use_responsive_link_target";
import {IFiltersFormValues, PropertyListFiltersModal} from "../../../property/components/PropertyListFiltersModal";
import {PropertyListSortOptionsModal} from "../../../property/components/PropertyListSortOptionsModal";
import {propertyListSortOptions} from "../../../property/constants/property_list_sort_options";
import {usePropertyList} from "../../../property_list/hooks/use_property_list";
import {ISearchFiltersFormValues} from "../../../property_list/PropertyListFilters";
import {PropertyListTable} from "../../../property_list/PropertyListTable";
import {PropertyListType} from "../../../property_list/PropertyListTabSwitcher";
import {PropertyListTileList} from "../../../property_list/PropertyListTileList";
import {getAmplitudeOfferSelectItemEventProperties} from "../../../tracking/amplitude/get_amplitude_offer_select_item_event_properties";
import {IAmplitudeItemListContext} from "../../../tracking/amplitude/get_amplitude_view_item_event_properties";
import {getCurrency} from "../../../utils/get_currency";
import {getThumbnailLink} from "../../../utils/get_thumbnail_link";
import {ILatestQuery} from "../../../utils/latest_query";
import {PropertyListTabTypes} from "../../detail/actions/fetch_offer_detail_properties";
import {propertyListTableFiltersFields} from "../../detail/components/property_list/OfferDetailPropertyListTable";
import {useOfferLink} from "../../detail/hooks/use_offer_link";
import {SortOptionsValues} from "../../detail/types/SortOptionsValues";
import {IUseMapOfferListQueryResult} from "../../hooks/use_map_offer_list_query";
import {DEFAULT_PROPERTY_LIST_MODAL_PAGE_SIZE} from "../../list/components/OfferListPropertiesModal";
import {createOfferListingAmplitudePendingDetailUrl} from "../../list/helpers/offer_listing_amplitude_item_list_id";
import {IMapOfferSidebar} from "../../map/types/MapOfferSidebar";
import {
    getMobileFiltersLabelForTracking,
    hitOfferModalMultiFiltersApply,
    hitOfferModalMultiFiltersClear,
    trackFilterApplyForCategories,
    trackFilterClearForCategories,
    trackFilterOpenForCategory
} from "../../tracking/hit_offer_modal_filters";
import {IOfferDetail} from "../../types/IOfferDetail";
import {transformMapFilterValues} from "../../utils/transform_map_filters_values";
import {MapOfferExpandablePanelOfferSwitcher} from "./MapOfferExpandablePanelOfferSwitcher";

export interface IMapOfferPanelListQuery {
    page: number;
    sort: string | null;
    filters: Record<string, IQueryValue>;
}

interface IProps {
    offer: IMapOfferSidebar;
    handleSetOfferId: (offerId: number | null) => void;
    latestQuery: ILatestQuery;
    mapOfferListQuery?: IUseMapOfferListQueryResult;
    handleQuickViewOpen: (id: number) => void;
    onPropertyListTypeClick: (value: PropertyListType) => void;
    propertyListType: PropertyListType;
    amplitudeItemListContext?: IAmplitudeItemListContext | null;
    propertiesAmplitudeItemListContext?: IAmplitudeItemListContext | null;
    onListQueryChange?: (listQuery: IMapOfferPanelListQuery) => void;
    offerIndex?: number;
}

export const MapOfferExpandablePanelOffer = (props: IProps) => {
    const {
        offer,
        handleSetOfferId,
        latestQuery,
        mapOfferListQuery,
        handleQuickViewOpen,
        propertyListType,
        onPropertyListTypeClick,
        amplitudeItemListContext,
        propertiesAmplitudeItemListContext,
        onListQueryChange,
        offerIndex = 0
    } = props;

    const latestSearchFilters = fromQueryValues<ISearchFiltersFormValues>(omit(propertyListTableFiltersFields, ["price"]), latestQuery);
    const propertyListFilters = isEmpty(latestSearchFilters) ? {} : toQueryValues(propertyListTableFiltersFields, latestSearchFilters);

    const propertyList = usePropertyList(offer.id, PropertyListTabTypes.MODAL, undefined, DEFAULT_PROPERTY_LIST_MODAL_PAGE_SIZE);
    const {page, pageCount, requestState, properties, sort, filters, onPageChange, pageIsLoading, onSortChange, onFiltersChange} = propertyList;

    const {isMobile} = useUserDevice();
    const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
    const [isSortModalOpen, setIsSortModalOpen] = useState(false);
    const headerRef = useRef<HTMLDivElement>(null);

    const offerLink = useOfferLink(offer);
    const linkTarget = useResponsiveLinkTarget();

    const isInitialListQueryReportRef = useRef(true);
    const onListQueryChangeRef = useRef(onListQueryChange);
    onListQueryChangeRef.current = onListQueryChange;

    useEffect(() => {
        if (isInitialListQueryReportRef.current) {
            isInitialListQueryReportRef.current = false;
            return;
        }

        onListQueryChangeRef.current?.({page: page.currentPage, sort: sort.currentSort, filters: filters.currentFilters});
    }, [page.currentPage, sort.currentSort, filters.currentFilters]);

    const handleOfferLinkClick = usePrimaryClick((event: MouseEvent<HTMLAnchorElement>) => {
        if (amplitudeItemListContext?.itemListId) {
            trackAmplitudeEvent("select_item", {
                ...getAmplitudeOfferSelectItemEventProperties({
                    offer,
                    index: offerIndex,
                    itemListId: amplitudeItemListContext.itemListId,
                    itemListName: amplitudeItemListContext.itemListName
                }),
                location: "listing_map"
            });
        }

        event.currentTarget.href = createOfferListingAmplitudePendingDetailUrl({
            url: event.currentTarget.href,
            itemId: offer.id,
            itemListContext: amplitudeItemListContext
        });
    });

    const handlers = {
        closeFilters: (values?: IFiltersFormValues) => {
            const valuesToClear = transformMapFilterValues(values);
            const trackLabels = getMobileFiltersLabelForTracking({...values, floor_choices: valuesToClear?.floor_choices || []});
            hitOfferModalMultiFiltersClear({eventName: "map_filters", filterCategory: trackLabels.categories, customLabel: trackLabels.options});
            if (valuesToClear) {
                onFiltersChange(
                    Object.keys(valuesToClear).reduce((acc, key) => {
                        return {...acc, [key]: undefined};
                    }, {})
                );
            }
            setIsFiltersModalOpen(false);
        },
        saveFilters: (values: IFiltersFormValues) => {
            const valuestToSave = transformMapFilterValues(values);
            onFiltersChange(valuestToSave);

            const trackLabels = getMobileFiltersLabelForTracking({...values, floor_choices: valuestToSave?.floor_choices || []});
            hitOfferModalMultiFiltersApply({eventName: "map_filters", filterCategory: trackLabels.categories, customLabel: trackLabels.options});
            setIsFiltersModalOpen(false);
        },
        saveSort: (value: number) => {
            const sortValue = propertyListSortOptions.find((option) => option.value === value);
            const sortValueSlug = sortValue?.slug || null;
            const sortValueLabel = sortValue?.label || "";
            trackFilterApplyForCategories({filterCategory: "Sortuj", eventName: "map_filters", customLabel: sortValueLabel});
            onSortChange(sortValueSlug);
            setIsSortModalOpen(false);
        },
        closeSort: () => {
            setIsSortModalOpen(false);
        },
        openSort: () => {
            trackFilterOpenForCategory({filterCategory: "Sortuj", eventName: "map_filters"});
            setIsSortModalOpen(true);
        },
        openFilters: () => {
            trackFilterOpenForCategory({filterCategory: "filters", eventName: "map_filters"});
            setIsFiltersModalOpen(true);
        },
        clearSort: () => {
            const sortSlugValue = propertyListSortOptions.find((option) => option.value === SortOptionsValues.LOWEST_PRICE)?.slug || null;
            const activeSort = propertyListSortOptions.find((option) => option.slug === sort.currentSort);
            trackFilterClearForCategories({eventName: "map_filters", filterCategory: "Sortuj", customLabel: activeSort?.label});
            onSortChange(sortSlugValue, propertyListFilters);
            setIsSortModalOpen(false);
        },
        handlePageChange: async (page: number) => {
            onPageChange(page);
            headerRef.current?.scrollIntoView({behavior: "smooth"});
        }
    };

    return (
        <Suspense fallback={<Loader />}>
            <div className={wrapper}>
                <div className={headerStyle}>
                    <Image alt="logo dewelopera" width="72px" height="54px" src={getThumbnailLink(offer.vendor.logo_url, 120, 90)} />

                    <MapOfferExpandablePanelOfferSwitcher
                        mapOfferListQuery={mapOfferListQuery}
                        offerId={offer.id}
                        handleSetOfferId={handleSetOfferId}
                        vendorId={offer.vendor.id}
                        propertyListType={propertyListType}
                    />
                </div>

                <div className={contentStyle}>
                    <a href={offerLink} target={linkTarget} onMouseDown={handleOfferLinkClick} className={infoBoxLink} title={offer.name}>
                        <Image alt={offer.name} src={getThumbnailLink(offer.main_image_url, 224, 147)} width="123px" height="116px" loading="lazy" />

                        <div className={infoBoxContainer}>
                            <Text as="div" variant="info_txt_2" className={constructionDatePill}>
                                <CalendarCheckIcon size="1.2" wrapperSize="1.2" wrapperColor="transparent" />

                                <Text as="span" variant="info_txt_2" strong>
                                    {formatFutureDate(offer.construction_date_range.upper, dateTimeFormat.quarter)}
                                </Text>
                            </Text>

                            <Text>{offer.name}</Text>

                            {!!offer.stats?.ranges_price_min && (
                                <Text as="div" variant="body_copy_1" mb={1.5} strong>
                                    <Highlight>
                                        {`od ${numberFormat(offer.stats.ranges_price_min)}`} {getCurrency({country: offer.region.country})}
                                    </Highlight>
                                </Text>
                            )}

                            <Text variant="info_txt_2">{offer.address}</Text>

                            {!!offer.stats?.distance_from_region.distance && (
                                <Text variant="info_txt_2" strong>{`(${offer.stats.distance_from_region.distance} km od centrum)`}</Text>
                            )}
                        </div>
                    </a>

                    <div className={tilesHeaderWrapper} ref={headerRef}>
                        <div className={headerButtonContainer}>
                            <Button
                                variant="none_secondary"
                                onClick={() => onPropertyListTypeClick(PropertyListType.TILES)}
                                iconLeft={PropertyPlanIcon}
                                className={cx(listTypeButton, propertyListType === PropertyListType.TABLE && listTypeActiveButton)}
                            >
                                Rzuty
                            </Button>

                            <Button
                                variant="none_secondary"
                                onClick={() => onPropertyListTypeClick(PropertyListType.TABLE)}
                                iconLeft={HorizontalListIcon}
                                className={cx(listTypeButton, propertyListType === PropertyListType.TILES && listTypeActiveButton)}
                            >
                                Lista
                            </Button>
                        </div>

                        <div className={headerButtonContainer}>
                            <div className={openFiltersButtonStyle} onClick={handlers.openFilters} data-testid={BUTTONS_TEST_ID.OPEN_FILTERS_BUTTON}>
                                <FiltersIcon size="2.4" wrapperSize="2.4" wrapperColor="transparent" />
                            </div>
                            <SortIcon size="2.4" wrapperSize="2.4" wrapperColor="transparent" className={css({ml: 1})} onClick={handlers.openSort} />
                        </div>
                    </div>

                    {propertyListType === PropertyListType.TABLE && (
                        <PropertyListTable
                            requestState={requestState}
                            offer={offer}
                            properties={properties}
                            sort={sort.currentSort}
                            onSortChange={(value) => onSortChange(value, propertyListFilters)}
                            onPageChange={handlers.handlePageChange}
                            pageCount={pageCount}
                            pageIsLoading={pageIsLoading}
                            currentPage={page.currentPage}
                            className={modalPropertyList}
                            hideCell={{
                                propertyNumber: true
                            }}
                            withoutLink
                            handleCellClick={handleQuickViewOpen}
                            textLinkButton
                            loaderWrapperBackground="var(--colors-gray-100)"
                            amplitudeItemListContext={propertiesAmplitudeItemListContext}
                            trackingMeta={{
                                listId: "zobacz_na_mapie",
                                listName: "Zobacz na mapie"
                            }}
                        />
                    )}

                    {propertyListType === PropertyListType.TILES && (
                        <PropertyListTileList
                            requestState={requestState}
                            properties={properties}
                            offer={offer as unknown as IOfferDetail}
                            className={modalPropertyList}
                            gridLayoutCols={{md: 2, lg: 2}}
                            onPropertyTileClick={(property) => handleQuickViewOpen(properties.findIndex(({id}) => id === property.id))}
                            propertyBoxComponent="div"
                            amplitudeItemListContext={propertiesAmplitudeItemListContext}
                            trackingMeta={{
                                listId: "zobacz_na_mapie",
                                listName: "Zobacz na mapie"
                            }}
                            favouritePlacement="rp_favourite_listing_map"
                            favouriteLocation="listing_map"
                        />
                    )}

                    {pageCount > 0 && (
                        <Pagination
                            className={css({mb: 2})}
                            currentPage={page.currentPage}
                            pageCount={pageCount}
                            onPageChange={handlers.handlePageChange}
                            size={isMobile ? PS.SM : PS.MD}
                            siblingsCount={isMobile ? 0 : 1}
                        />
                    )}
                </div>
            </div>

            <PropertyListFiltersModal
                offerType={offer.type}
                isOpen={isFiltersModalOpen && !!isMobile}
                handleClose={handlers.closeFilters}
                handleConfirm={handlers.saveFilters}
            />

            <PropertyListSortOptionsModal
                onClear={handlers.clearSort}
                onSave={handlers.saveSort}
                onClose={handlers.closeSort}
                isOpen={isSortModalOpen}
                defaultValue={propertyListSortOptions.find((option) => option.slug === sort.currentSort)?.value || SortOptionsValues.LOWEST_PRICE}
            />
        </Suspense>
    );
};

const wrapper = css({
    width: "100%"
});

const headerStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    px: 1.5,
    backgroundColor: "var(--colors-white)"
});

const modalPropertyList = css({
    backgroundColor: "var(--colors-gray-100) !important"
});

const tilesHeaderWrapper = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%"
});

const listTypeButton = css({
    p: 1,

    "&:hover": {
        color: "var(--colors-secondary)",
        fill: "var(--colors-secondary)"
    }
});

const listTypeActiveButton = css({
    color: "var(--colors-gray-600)",
    fill: "var(--colors-gray-600)"
});

const headerButtonContainer = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "normal",
    gap: 2
});

// Opakowanie ikony filtrów: element z handlerem otwarcia panelu, zachowuje układ samej ikony
const openFiltersButtonStyle = css({
    display: "inline-flex"
});

const contentStyle = css({
    backgroundColor: "var(--colors-gray-100)",
    px: 1.5,
    maxHeight: "calc(100dvh - 60px - 54px)",
    overflowX: "hidden",
    overflowY: "auto",
    pb: 6
});

const infoBoxLink = css({
    display: "flex",
    alignItems: "flex-start",
    gap: 1,
    py: 2,
    color: "inherit",
    textDecoration: "none",

    "&:hover, &:focus": {
        color: "inherit",
        textDecoration: "none"
    }
});

const infoBoxContainer = css({
    display: "flex",
    alignItems: "flex-start",
    flexDirection: "column",
    gap: 0.5
});

const constructionDatePill = css({
    display: "flex",
    alignItems: "center",
    p: 0.5,
    gap: 0.5,
    borderRadius: 2,
    backgroundColor: "var(--colors-primary)"
});
import {MouseEvent, Suspense, useEffect, useRef, useState} from "react";
import {css, cx} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {CalendarCheckIcon, FiltersIcon, HorizontalListIcon, PropertyPlanIcon, SortIcon} from "@pg-design/icons-module";
import {Image} from "@pg-design/image-module";
import {Loader} from "@pg-design/loader-module";
import {Pagination, PaginationSize as PS} from "@pg-design/pagination-module";
import {Highlight, Text} from "@pg-design/text-module";
import {trackAmplitudeEvent} from "@pg-mono/amplitude";
import {fromQueryValues, toQueryValues} from "@pg-mono/form";
import {isEmpty, omit} from "@pg-mono/nodash";
import {IQueryValue} from "@pg-mono/request";
import {dateTimeFormat, formatFutureDate, numberFormat} from "@pg-mono/string-utils";
import {BUTTONS_TEST_ID} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";

import {usePrimaryClick} from "../../../hooks/use_primary_click";
import {useResponsiveLinkTarget} from "../../../hooks/use_responsive_link_target";
import {IFiltersFormValues, PropertyListFiltersModal} from "../../../property/components/PropertyListFiltersModal";
import {PropertyListSortOptionsModal} from "../../../property/components/PropertyListSortOptionsModal";
import {propertyListSortOptions} from "../../../property/constants/property_list_sort_options";
import {usePropertyList} from "../../../property_list/hooks/use_property_list";
import {ISearchFiltersFormValues} from "../../../property_list/PropertyListFilters";
import {PropertyListTable} from "../../../property_list/PropertyListTable";
import {PropertyListType} from "../../../property_list/PropertyListTabSwitcher";
import {PropertyListTileList} from "../../../property_list/PropertyListTileList";
import {getAmplitudeOfferSelectItemEventProperties} from "../../../tracking/amplitude/get_amplitude_offer_select_item_event_properties";
import {IAmplitudeItemListContext} from "../../../tracking/amplitude/get_amplitude_view_item_event_properties";
import {getCurrency} from "../../../utils/get_currency";
import {getThumbnailLink} from "../../../utils/get_thumbnail_link";
import {ILatestQuery} from "../../../utils/latest_query";
import {PropertyListTabTypes} from "../../detail/actions/fetch_offer_detail_properties";
import {propertyListTableFiltersFields} from "../../detail/components/property_list/OfferDetailPropertyListTable";
import {useOfferLink} from "../../detail/hooks/use_offer_link";
import {SortOptionsValues} from "../../detail/types/SortOptionsValues";
import {IUseMapOfferListQueryResult} from "../../hooks/use_map_offer_list_query";
import {DEFAULT_PROPERTY_LIST_MODAL_PAGE_SIZE} from "../../list/components/OfferListPropertiesModal";
import {createOfferListingAmplitudePendingDetailUrl} from "../../list/helpers/offer_listing_amplitude_item_list_id";
import {IMapOfferSidebar} from "../../map/types/MapOfferSidebar";
import {
    getMobileFiltersLabelForTracking,
    hitOfferModalMultiFiltersApply,
    hitOfferModalMultiFiltersClear,
    trackFilterApplyForCategories,
    trackFilterClearForCategories,
    trackFilterOpenForCategory
} from "../../tracking/hit_offer_modal_filters";
import {IOfferDetail} from "../../types/IOfferDetail";
import {transformMapFilterValues} from "../../utils/transform_map_filters_values";
import {MapOfferExpandablePanelOfferSwitcher} from "./MapOfferExpandablePanelOfferSwitcher";

export interface IMapOfferPanelListQuery {
    page: number;
    sort: string | null;
    filters: Record<string, IQueryValue>;
}

interface IProps {
    offer: IMapOfferSidebar;
    handleSetOfferId: (offerId: number | null) => void;
    latestQuery: ILatestQuery;
    mapOfferListQuery?: IUseMapOfferListQueryResult;
    handleQuickViewOpen: (id: number) => void;
    onPropertyListTypeClick: (value: PropertyListType) => void;
    propertyListType: PropertyListType;
    amplitudeItemListContext?: IAmplitudeItemListContext | null;
    propertiesAmplitudeItemListContext?: IAmplitudeItemListContext | null;
    onListQueryChange?: (listQuery: IMapOfferPanelListQuery) => void;
    offerIndex?: number;
}

export const MapOfferExpandablePanelOffer = (props: IProps) => {
    const {
        offer,
        handleSetOfferId,
        latestQuery,
        mapOfferListQuery,
        handleQuickViewOpen,
        propertyListType,
        onPropertyListTypeClick,
        amplitudeItemListContext,
        propertiesAmplitudeItemListContext,
        onListQueryChange,
        offerIndex = 0
    } = props;

    const latestSearchFilters = fromQueryValues<ISearchFiltersFormValues>(omit(propertyListTableFiltersFields, ["price"]), latestQuery);
    const propertyListFilters = isEmpty(latestSearchFilters) ? {} : toQueryValues(propertyListTableFiltersFields, latestSearchFilters);

    const propertyList = usePropertyList(offer.id, PropertyListTabTypes.MODAL, undefined, DEFAULT_PROPERTY_LIST_MODAL_PAGE_SIZE);
    const {page, pageCount, requestState, properties, sort, filters, onPageChange, pageIsLoading, onSortChange, onFiltersChange} = propertyList;

    const {isMobile} = useUserDevice();
    const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
    const [isSortModalOpen, setIsSortModalOpen] = useState(false);
    const headerRef = useRef<HTMLDivElement>(null);

    const offerLink = useOfferLink(offer);
    const linkTarget = useResponsiveLinkTarget();

    const isInitialListQueryReportRef = useRef(true);
    const onListQueryChangeRef = useRef(onListQueryChange);
    onListQueryChangeRef.current = onListQueryChange;

    useEffect(() => {
        if (isInitialListQueryReportRef.current) {
            isInitialListQueryReportRef.current = false;
            return;
        }

        onListQueryChangeRef.current?.({page: page.currentPage, sort: sort.currentSort, filters: filters.currentFilters});
    }, [page.currentPage, sort.currentSort, filters.currentFilters]);

    const handleOfferLinkClick = usePrimaryClick((event: MouseEvent<HTMLAnchorElement>) => {
        if (amplitudeItemListContext?.itemListId) {
            trackAmplitudeEvent("select_item", {
                ...getAmplitudeOfferSelectItemEventProperties({
                    offer,
                    index: offerIndex,
                    itemListId: amplitudeItemListContext.itemListId,
                    itemListName: amplitudeItemListContext.itemListName
                }),
                location: "listing_map"
            });
        }

        event.currentTarget.href = createOfferListingAmplitudePendingDetailUrl({
            url: event.currentTarget.href,
            itemId: offer.id,
            itemListContext: amplitudeItemListContext
        });
    });

    const handlers = {
        closeFilters: (values?: IFiltersFormValues) => {
            const valuesToClear = transformMapFilterValues(values);
            const trackLabels = getMobileFiltersLabelForTracking({...values, floor_choices: valuesToClear?.floor_choices || []});
            hitOfferModalMultiFiltersClear({eventName: "map_filters", filterCategory: trackLabels.categories, customLabel: trackLabels.options});
            if (valuesToClear) {
                onFiltersChange(
                    Object.keys(valuesToClear).reduce((acc, key) => {
                        return {...acc, [key]: undefined};
                    }, {})
                );
            }
            setIsFiltersModalOpen(false);
        },
        saveFilters: (values: IFiltersFormValues) => {
            const valuestToSave = transformMapFilterValues(values);
            onFiltersChange(valuestToSave);

            const trackLabels = getMobileFiltersLabelForTracking({...values, floor_choices: valuestToSave?.floor_choices || []});
            hitOfferModalMultiFiltersApply({eventName: "map_filters", filterCategory: trackLabels.categories, customLabel: trackLabels.options});
            setIsFiltersModalOpen(false);
        },
        saveSort: (value: number) => {
            const sortValue = propertyListSortOptions.find((option) => option.value === value);
            const sortValueSlug = sortValue?.slug || null;
            const sortValueLabel = sortValue?.label || "";
            trackFilterApplyForCategories({filterCategory: "Sortuj", eventName: "map_filters", customLabel: sortValueLabel});
            onSortChange(sortValueSlug);
            setIsSortModalOpen(false);
        },
        closeSort: () => {
            setIsSortModalOpen(false);
        },
        openSort: () => {
            trackFilterOpenForCategory({filterCategory: "Sortuj", eventName: "map_filters"});
            setIsSortModalOpen(true);
        },
        openFilters: () => {
            trackFilterOpenForCategory({filterCategory: "filters", eventName: "map_filters"});
            setIsFiltersModalOpen(true);
        },
        clearSort: () => {
            const sortSlugValue = propertyListSortOptions.find((option) => option.value === SortOptionsValues.LOWEST_PRICE)?.slug || null;
            const activeSort = propertyListSortOptions.find((option) => option.slug === sort.currentSort);
            trackFilterClearForCategories({eventName: "map_filters", filterCategory: "Sortuj", customLabel: activeSort?.label});
            onSortChange(sortSlugValue, propertyListFilters);
            setIsSortModalOpen(false);
        },
        handlePageChange: async (page: number) => {
            onPageChange(page);
            headerRef.current?.scrollIntoView({behavior: "smooth"});
        }
    };

    return (
        <Suspense fallback={<Loader />}>
            <div className={wrapper}>
                <div className={headerStyle}>
                    <Image alt="logo dewelopera" width="72px" height="54px" src={getThumbnailLink(offer.vendor.logo_url, 120, 90)} />

                    <MapOfferExpandablePanelOfferSwitcher
                        mapOfferListQuery={mapOfferListQuery}
                        offerId={offer.id}
                        handleSetOfferId={handleSetOfferId}
                        vendorId={offer.vendor.id}
                        propertyListType={propertyListType}
                    />
                </div>

                <div className={contentStyle}>
                    <a href={offerLink} target={linkTarget} onMouseDown={handleOfferLinkClick} className={infoBoxLink} title={offer.name}>
                        <Image alt={offer.name} src={getThumbnailLink(offer.main_image_url, 224, 147)} width="123px" height="116px" loading="lazy" />

                        <div className={infoBoxContainer}>
                            <Text as="div" variant="info_txt_2" className={constructionDatePill}>
                                <CalendarCheckIcon size="1.2" wrapperSize="1.2" wrapperColor="transparent" />

                                <Text as="span" variant="info_txt_2" strong>
                                    {formatFutureDate(offer.construction_date_range.upper, dateTimeFormat.quarter)}
                                </Text>
                            </Text>

                            <Text>{offer.name}</Text>

                            {!!offer.stats?.ranges_price_min && (
                                <Text as="div" variant="body_copy_1" mb={1.5} strong>
                                    <Highlight>
                                        {`od ${numberFormat(offer.stats.ranges_price_min)}`} {getCurrency({country: offer.region.country})}
                                    </Highlight>
                                </Text>
                            )}

                            <Text variant="info_txt_2">{offer.address}</Text>

                            {!!offer.stats?.distance_from_region.distance && (
                                <Text variant="info_txt_2" strong>{`(${offer.stats.distance_from_region.distance} km od centrum)`}</Text>
                            )}
                        </div>
                    </a>

                    <div className={tilesHeaderWrapper} ref={headerRef}>
                        <div className={headerButtonContainer}>
                            <Button
                                variant="none_secondary"
                                onClick={() => onPropertyListTypeClick(PropertyListType.TILES)}
                                iconLeft={PropertyPlanIcon}
                                className={cx(listTypeButton, propertyListType === PropertyListType.TABLE && listTypeActiveButton)}
                            >
                                Rzuty
                            </Button>

                            <Button
                                variant="none_secondary"
                                onClick={() => onPropertyListTypeClick(PropertyListType.TABLE)}
                                iconLeft={HorizontalListIcon}
                                className={cx(listTypeButton, propertyListType === PropertyListType.TILES && listTypeActiveButton)}
                            >
                                Lista
                            </Button>
                        </div>

                        <div className={headerButtonContainer}>
                            <div className={openFiltersButtonStyle} onClick={handlers.openFilters} data-testid={BUTTONS_TEST_ID.OPEN_FILTERS_BUTTON}>
                                <FiltersIcon size="2.4" wrapperSize="2.4" wrapperColor="transparent" />
                            </div>
                            <SortIcon size="2.4" wrapperSize="2.4" wrapperColor="transparent" className={css({ml: 1})} onClick={handlers.openSort} />
                        </div>
                    </div>

                    {propertyListType === PropertyListType.TABLE && (
                        <PropertyListTable
                            requestState={requestState}
                            offer={offer}
                            properties={properties}
                            sort={sort.currentSort}
                            onSortChange={(value) => onSortChange(value, propertyListFilters)}
                            onPageChange={handlers.handlePageChange}
                            pageCount={pageCount}
                            pageIsLoading={pageIsLoading}
                            currentPage={page.currentPage}
                            className={modalPropertyList}
                            hideCell={{
                                propertyNumber: true
                            }}
                            withoutLink
                            handleCellClick={handleQuickViewOpen}
                            textLinkButton
                            loaderWrapperBackground="var(--colors-gray-100)"
                            amplitudeItemListContext={propertiesAmplitudeItemListContext}
                            trackingMeta={{
                                listId: "zobacz_na_mapie",
                                listName: "Zobacz na mapie"
                            }}
                        />
                    )}

                    {propertyListType === PropertyListType.TILES && (
                        <PropertyListTileList
                            requestState={requestState}
                            properties={properties}
                            offer={offer as unknown as IOfferDetail}
                            className={modalPropertyList}
                            gridLayoutCols={{md: 2, lg: 2}}
                            onPropertyTileClick={(property) => handleQuickViewOpen(properties.findIndex(({id}) => id === property.id))}
                            propertyBoxComponent="div"
                            amplitudeItemListContext={propertiesAmplitudeItemListContext}
                            trackingMeta={{
                                listId: "zobacz_na_mapie",
                                listName: "Zobacz na mapie"
                            }}
                            favouritePlacement="rp_favourite_listing_map"
                            favouriteLocation="listing_map"
                        />
                    )}

                    {pageCount > 0 && (
                        <Pagination
                            className={css({mb: 2})}
                            currentPage={page.currentPage}
                            pageCount={pageCount}
                            onPageChange={handlers.handlePageChange}
                            size={isMobile ? PS.SM : PS.MD}
                            siblingsCount={isMobile ? 0 : 1}
                        />
                    )}
                </div>
            </div>

            <PropertyListFiltersModal
                offerType={offer.type}
                isOpen={isFiltersModalOpen && !!isMobile}
                handleClose={handlers.closeFilters}
                handleConfirm={handlers.saveFilters}
            />

            <PropertyListSortOptionsModal
                onClear={handlers.clearSort}
                onSave={handlers.saveSort}
                onClose={handlers.closeSort}
                isOpen={isSortModalOpen}
                defaultValue={propertyListSortOptions.find((option) => option.slug === sort.currentSort)?.value || SortOptionsValues.LOWEST_PRICE}
            />
        </Suspense>
    );
};

const wrapper = css({
    width: "100%"
});

const headerStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    px: 1.5,
    backgroundColor: "var(--colors-white)"
});

const modalPropertyList = css({
    backgroundColor: "var(--colors-gray-100) !important"
});

const tilesHeaderWrapper = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%"
});

const listTypeButton = css({
    p: 1,

    "&:hover": {
        color: "var(--colors-secondary)",
        fill: "var(--colors-secondary)"
    }
});

const listTypeActiveButton = css({
    color: "var(--colors-gray-600)",
    fill: "var(--colors-gray-600)"
});

const headerButtonContainer = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "normal",
    gap: 2
});

// Opakowanie ikony filtrów: element z handlerem otwarcia panelu, zachowuje układ samej ikony
const openFiltersButtonStyle = css({
    display: "inline-flex"
});

const contentStyle = css({
    backgroundColor: "var(--colors-gray-100)",
    px: 1.5,
    maxHeight: "calc(100dvh - 60px - 54px)",
    overflowX: "hidden",
    overflowY: "auto",
    pb: 6
});

const infoBoxLink = css({
    display: "flex",
    alignItems: "flex-start",
    gap: 1,
    py: 2,
    color: "inherit",
    textDecoration: "none",

    "&:hover, &:focus": {
        color: "inherit",
        textDecoration: "none"
    }
});

const infoBoxContainer = css({
    display: "flex",
    alignItems: "flex-start",
    flexDirection: "column",
    gap: 0.5
});

const constructionDatePill = css({
    display: "flex",
    alignItems: "center",
    p: 0.5,
    gap: 0.5,
    borderRadius: 2,
    backgroundColor: "var(--colors-primary)"
});
